import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

// Legacy short-key groups that existed before sub-teams shipped.
// Kept so old KPI assignments referencing these slugs don't go dark.
const LEGACY_GROUPS = {
  driver:      { label: 'Drivers',      color: '#3b82f6' },
  sales:       { label: 'Sales',        color: '#10b981' },
  dispatcher:  { label: 'Dispatchers',  color: '#f59e0b' },
  admin:       { label: 'Admins',       color: '#8b5cf6' },
  maintenance: { label: 'Maintenance',  color: '#6b7280' },
};

const LEGACY_ORDER = ['driver', 'sales', 'dispatcher', 'admin', 'maintenance'];

// Client-side mirror of server/utils/teamHierarchy.js slugifyTeamName.
// Keep in lockstep — the slug is what the leaderboard access gate
// matches against on /api/leaderboard/:groupName.
function slugifyTeamName(teamName) {
  if (!teamName) return '';
  const s = String(teamName).trim();
  if (s === 'Driver Operations') return 'driver';
  return s.replace(/\s+(Drivers|Operations)\s*$/i, '')
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export default function GroupAssignmentPage() {
  const [assignments, setAssignments] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // KPI assignment modal
  const [showKpiModal, setShowKpiModal] = useState(false);
  const [kpiForm, setKpiForm] = useState({ kpi_id: '', group_name: 'driver', weight: 100, display_order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Member management modal
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberGroup, setMemberGroup] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [savingMembers, setSavingMembers] = useState(false);

  // Group members cache
  const [groupMembers, setGroupMembers] = useState({}); // { groupName: [user, ...] }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groups, kpiList, userList, teamList] = await Promise.all([
        api.getLeaderboardGroups(),
        api.getLeaderboardKpis(),
        api.getUsers(),
        api.getTeams().catch(() => []),
      ]);
      const assignmentList = Array.isArray(groups) ? groups : [];
      setAssignments(assignmentList);
      setKpis(Array.isArray(kpiList) ? kpiList : []);
      setAllUsers(Array.isArray(userList) ? userList : []);
      setTeams(Array.isArray(teamList) ? teamList : []);

      // Build full slug universe: legacy keys + team slugs + any
      // group_name that already has an assignment. We fetch members
      // for every slug so empty groups still render a Manage Members
      // button (chicken-and-egg: you'd never be able to add members
      // to a fresh group otherwise).
      const teamSlugs = (Array.isArray(teamList) ? teamList : [])
        .map(t => slugifyTeamName(t.name))
        .filter(Boolean);
      const allSlugs = [...new Set([
        ...Object.keys(LEGACY_GROUPS),
        ...teamSlugs,
        ...assignmentList.map(a => a.group_name),
      ])];

      const members = {};
      await Promise.all(allSlugs.map(async gn => {
        try {
          const m = await api.getLeaderboardMembers(gn);
          members[gn] = Array.isArray(m) ? m : [];
        } catch { members[gn] = []; }
      }));
      setGroupMembers(members);
    } catch { setAssignments([]); setKpis([]); setAllUsers([]); setTeams([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Dynamic groups (legacy + teams + any referenced in assignments) ──
  const orderedGroups = useMemo(() => {
    const map = {};

    // 1. Legacy groups (color + label fallback)
    for (const slug of Object.keys(LEGACY_GROUPS)) {
      map[slug] = {
        slug,
        label: LEGACY_GROUPS[slug].label,
        color: LEGACY_GROUPS[slug].color,
        parent_slug: null,
        legacy: true,
      };
    }

    // 2. Team-backed groups — override legacy entry if slug collides
    //    (e.g. 'driver' legacy ↔ 'Driver Operations' team)
    const teamById = {};
    for (const t of teams) teamById[t.id] = t;
    for (const t of teams) {
      const slug = slugifyTeamName(t.name);
      if (!slug) continue;
      const parentSlug = t.parent_team_id && teamById[t.parent_team_id]
        ? slugifyTeamName(teamById[t.parent_team_id].name)
        : null;
      const parentColor = parentSlug
        ? (map[parentSlug]?.color || LEGACY_GROUPS[parentSlug]?.color)
        : null;
      map[slug] = {
        slug,
        label: t.name,
        color: LEGACY_GROUPS[slug]?.color || parentColor || '#64748b',
        parent_slug: parentSlug,
        team_id: t.id,
        legacy: false,
      };
    }

    // 3. Orphan group_names in assignments that don't match anything
    for (const a of assignments) {
      if (!map[a.group_name]) {
        map[a.group_name] = {
          slug: a.group_name, label: a.group_name, color: '#64748b',
          parent_slug: null, legacy: true,
        };
      }
    }

    // Flatten into tree order: parents first (legacy order, then alpha),
    // each parent followed by its children (alpha)
    const list = Object.values(map);
    const parents = list.filter(g => !g.parent_slug);
    const childrenByParent = {};
    for (const g of list) {
      if (g.parent_slug) {
        (childrenByParent[g.parent_slug] = childrenByParent[g.parent_slug] || []).push(g);
      }
    }
    parents.sort((a, b) => {
      const ai = LEGACY_ORDER.indexOf(a.slug);
      const bi = LEGACY_ORDER.indexOf(b.slug);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a.label.localeCompare(b.label);
    });

    const out = [];
    for (const p of parents) {
      out.push(p);
      const kids = (childrenByParent[p.slug] || []).sort((a, b) => a.label.localeCompare(b.label));
      for (const c of kids) out.push(c);
    }
    return out;
  }, [teams, assignments]);

  const groupLabel = (slug) => orderedGroups.find(g => g.slug === slug)?.label || slug;
  const groupColor = (slug) => orderedGroups.find(g => g.slug === slug)?.color || '#64748b';
  const defaultGroupSlug = orderedGroups[0]?.slug || 'driver';

  // ── KPI Assignment ──
  function openAddKpi() {
    setEditingId(null);
    setKpiForm({ kpi_id: kpis[0]?.id || '', group_name: defaultGroupSlug, weight: 100, display_order: 0 });
    setError('');
    setShowKpiModal(true);
  }

  function openEditKpi(a) {
    setEditingId(a.id);
    setKpiForm({ kpi_id: a.kpi_id, group_name: a.group_name, weight: a.weight, display_order: a.display_order });
    setError('');
    setShowKpiModal(true);
  }

  async function handleSaveKpi(e) {
    e.preventDefault();
    if (!kpiForm.kpi_id) { setError('Select a KPI'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.updateLeaderboardGroup(editingId, kpiForm);
        setSuccess('Assignment updated');
      } else {
        await api.createLeaderboardGroup(kpiForm);
        setSuccess('KPI assigned to group');
      }
      setShowKpiModal(false);
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message || 'Failed to save'); }
    setSaving(false);
  }

  async function handleDeleteKpi(id) {
    try {
      await api.deleteLeaderboardGroup(id);
      load();
      setSuccess('Assignment removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch { /* */ }
  }

  // ── Member Management ──
  function openMembers(groupName) {
    setMemberGroup(groupName);
    const current = groupMembers[groupName] || [];
    setMemberIds(current.map(m => m.id));
    setMemberSearch('');
    setError('');
    setShowMemberModal(true);
  }

  function toggleMember(userId) {
    setMemberIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  }

  async function saveMembers() {
    setSavingMembers(true);
    setError('');
    try {
      await api.setLeaderboardMembers(memberGroup, memberIds);
      setSuccess(`${memberIds.length} members assigned to ${groupLabel(memberGroup)}`);
      setShowMemberModal(false);
      const m = await api.getLeaderboardMembers(memberGroup);
      setGroupMembers(prev => ({ ...prev, [memberGroup]: Array.isArray(m) ? m : [] }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message || 'Failed to save members'); }
    setSavingMembers(false);
  }

  // Group assignments by group_name
  const grouped = {};
  for (const a of assignments) {
    if (!grouped[a.group_name]) grouped[a.group_name] = [];
    grouped[a.group_name].push(a);
  }

  // Weight warnings
  function getWeightWarning(groupName) {
    const items = grouped[groupName] || [];
    const byPeriod = {};
    for (const a of items) {
      const pt = a.period_type || 'unknown';
      byPeriod[pt] = (byPeriod[pt] || 0) + a.weight;
    }
    const warnings = [];
    for (const [pt, total] of Object.entries(byPeriod)) {
      if (Math.abs(total - 100) > 0.1) warnings.push(`${pt}: weights = ${total}% (should be 100%)`);
    }
    return warnings;
  }

  // Filtered users for member picker
  const filteredUsers = allUsers.filter(u => {
    if (!u.is_active) return false;
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return u.full_name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q);
  });

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Groups</h1>
        <button className="btn btn-gold" onClick={openAddKpi} disabled={kpis.length === 0}>+ Assign KPI</button>
      </div>

      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
        Manage who's on each leaderboard and which KPIs they're measured on. Add members first, then assign KPIs.
      </p>

      {kpis.length === 0 && !loading && (
        <div style={{ padding: '16px', background: '#fffbeb', borderRadius: '8px', fontSize: '13px', color: '#92400e', marginBottom: '16px' }}>
          No KPIs created yet. <a href="/admin-mod/leaderboard-kpis" style={{ color: '#d4a843', fontWeight: 600 }}>Create KPIs first</a>.
        </div>
      )}

      {error && !showKpiModal && !showMemberModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <div>
          {orderedGroups.map(g => {
            const items = grouped[g.slug] || [];
            const warnings = getWeightWarning(g.slug);
            const members = groupMembers[g.slug] || [];
            const isSubTeam = !!g.parent_slug;

            // Hide legacy groups that have no KPIs AND no members AND aren't
            // backed by a team. Team-backed groups always show so you can
            // set them up before any KPI is assigned.
            if (g.legacy && !g.team_id && items.length === 0 && members.length === 0) {
              return null;
            }

            return (
              <div key={g.slug} style={{
                marginBottom: '24px', background: '#fff', borderRadius: '12px',
                padding: '20px', border: '1px solid #e2e8f0',
                marginLeft: isSubTeam ? '32px' : 0,
                borderLeft: isSubTeam ? `3px solid ${g.color}` : '1px solid #e2e8f0',
              }}>
                {/* Group header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: isSubTeam ? '15px' : '17px', fontWeight: 800, color: g.color }}>
                      {isSubTeam ? '↳ ' : ''}{g.label}
                    </h2>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {items.length} KPI{items.length !== 1 ? 's' : ''}
                    </span>
                    {g.team_id && (
                      <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', padding: '2px 7px', borderRadius: '4px', background: `${g.color}15`, color: g.color, textTransform: 'uppercase' }}>
                        Team
                      </span>
                    )}
                    {g.legacy && !g.team_id && (
                      <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', padding: '2px 7px', borderRadius: '4px', background: '#f1f5f9', color: '#64748b', textTransform: 'uppercase' }}>
                        Legacy
                      </span>
                    )}
                  </div>
                  <button className="btn btn-sm btn-outline" onClick={() => openMembers(g.slug)} style={{ fontWeight: 600 }}>
                    Manage Members ({members.length})
                  </button>
                </div>

                {/* Members strip */}
                {members.length === 0 ? (
                  <div style={{ padding: '10px 14px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e', marginBottom: '14px' }}>
                    No members assigned — add users to this group to populate the leaderboard.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                    {members.slice(0, 20).map(m => (
                      <span key={m.id} style={{
                        fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '6px',
                        background: `${g.color}10`, color: g.color, border: `1px solid ${g.color}25`,
                      }}>
                        {m.full_name}
                      </span>
                    ))}
                    {members.length > 20 && (
                      <span style={{ fontSize: '11px', color: '#94a3b8', padding: '3px 6px' }}>+{members.length - 20} more</span>
                    )}
                  </div>
                )}

                {warnings.length > 0 && (
                  <div style={{ padding: '8px 12px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e', marginBottom: '10px' }}>
                    {warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}

                {/* KPI table — only if any assigned */}
                {items.length > 0 ? (
                  <div className="table-responsive">
                    <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>KPI</th>
                          <th>Period</th>
                          <th>Weight</th>
                          <th>Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(a => (
                          <tr key={a.id}>
                            <td style={{ fontWeight: 600 }}>{a.kpi_name}</td>
                            <td><span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: '#64748b' }}>{a.period_type}</span></td>
                            <td><strong>{a.weight}%</strong></td>
                            <td>{a.display_order}</td>
                            <td>
                              <button className="btn btn-sm btn-outline" onClick={() => openEditKpi(a)}>Edit</button>
                              {' '}
                              <button className="btn btn-sm btn-danger" onClick={() => handleDeleteKpi(a.id)}>Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', padding: '6px 2px' }}>
                    No KPIs assigned yet — use "+ Assign KPI" above to add one.
                  </div>
                )}
              </div>
            );
          })}

          {orderedGroups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No groups yet. Create teams in Admin → Teams & Positions, or assign a KPI to a legacy group.
            </div>
          )}
        </div>
      )}

      {/* KPI Assignment Modal */}
      {showKpiModal && (
        <Modal title={editingId ? 'Edit KPI Assignment' : 'Assign KPI to Group'} onClose={() => setShowKpiModal(false)}>
          <form onSubmit={handleSaveKpi}>
            {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}
            <div className="form-grid">
              <div className="form-group">
                <label>KPI *</label>
                <select value={kpiForm.kpi_id} onChange={e => setKpiForm(f => ({ ...f, kpi_id: e.target.value }))} className="form-input" disabled={!!editingId}>
                  <option value="">Select KPI...</option>
                  {kpis.map(k => <option key={k.id} value={k.id}>{k.name} ({k.period_type})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Group *</label>
                <select value={kpiForm.group_name} onChange={e => setKpiForm(f => ({ ...f, group_name: e.target.value }))} className="form-input" disabled={!!editingId}>
                  {orderedGroups.map(g => (
                    <option key={g.slug} value={g.slug}>
                      {g.parent_slug ? `    ↳ ${g.label}` : g.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Weight (%)</label>
                <input type="number" min="0" max="100" step="0.1" value={kpiForm.weight} onChange={e => setKpiForm(f => ({ ...f, weight: parseFloat(e.target.value) || 0 }))} className="form-input" />
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>KPIs on the same period type should total 100%</div>
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input type="number" min="0" value={kpiForm.display_order} onChange={e => setKpiForm(f => ({ ...f, display_order: parseInt(e.target.value) || 0 }))} className="form-input" />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowKpiModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editingId ? 'Update' : 'Assign')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Member Management Modal */}
      {showMemberModal && (
        <Modal title={`Members — ${groupLabel(memberGroup)}`} onClose={() => setShowMemberModal(false)}>
          {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px' }}>
            All KPIs assigned to <strong>{groupLabel(memberGroup)}</strong> will use these members.
          </p>

          <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              className="form-input"
              placeholder="Search users..."
              style={{ flex: 1 }}
            />
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setMemberIds(allUsers.filter(u => u.is_active).map(u => u.id))}>Select All</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setMemberIds([])}>Clear</button>
          </div>

          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: 600 }}>
            {memberIds.length} user{memberIds.length !== 1 ? 's' : ''} selected
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            {filteredUsers.map(u => (
              <label
                key={u.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                  borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  background: memberIds.includes(u.id) ? '#eff6ff' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={memberIds.includes(u.id)}
                  onChange={() => toggleMember(u.id)}
                  style={{ width: '16px', height: '16px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{u.full_name}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{u.username} &middot; {u.role}</div>
                </div>
              </label>
            ))}
            {filteredUsers.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No users found</div>
            )}
          </div>

          <div className="modal-actions" style={{ marginTop: '16px' }}>
            <button type="button" className="btn btn-outline" onClick={() => setShowMemberModal(false)}>Cancel</button>
            <button type="button" className="btn btn-gold" onClick={saveMembers} disabled={savingMembers}>
              {savingMembers ? 'Saving...' : `Save Members (${memberIds.length})`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
